import type { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format } from 'app/shared/utils/string.util';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { GlobalVariables } from 'app/services/globals';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { parameterizeDynamicsControlMetadata } from '../consts/metadata-consts';
import { DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind } from 'shell-models-lib';
import { SSLMode, sslModeOptions, UseSystemTrustStoreMode, useSystemTrustStoreOptions } from '../consts/mysql-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';

const useSystemTrustStoreContol: ControlMetadata = {
    type: 'radio',
    bind: 'useSystemTrustStore',
    isLabelVertical: true,
    options: useSystemTrustStoreOptions,
    defaultValue: UseSystemTrustStoreMode.NotUse
};

const sslModeContol: ControlMetadata = {
    type: 'select-switch',
    bind: 'sslMode',
    label: stringResources.SSL_Mode,
    isLabelVertical: true,
    options: sslModeOptions,
    defaultValue: SSLMode.Verify_identity,
    cases: sslModeOptions.map((option) => ({
        caseValue: option.value,
        children: option.value === SSLMode.Disabled ? [] : [useSystemTrustStoreContol]
    }))
};

const connectionStringControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'server',
        label: stringResources.ModelName_ServerName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'port',
        label: stringResources.ModelName_Port,
        isLabelVertical: true,
        inputType: 'number',
        defaultValue: 3306,
        min: 0
    },
    {
        type: 'textbox',
        bind: 'databaseName',
        label: stringResources.ModelName_DatabaseName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'userName',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {

        type: 'dynamic-akv-switcher',
        hideAKVSwitcherInConnStrProps: true,
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            isLabelVertical: true,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.MariaDB_DisplayText),
            stringResources.ModelName_Password)
    }

];

const connectionStrAKVControl: ControlMetadata = {
    type: 'select-button-switch',
    required: true,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.ModelName_ConnectionString,
            value: false
        },
        {
            label: stringResources.AzureKeyVault_DisplayText,
            value: true,
            hideAKVOptionIfNotChosen: false
        }
    ],
    bind: 'enableAKV',
    isNotRelatedToCredential: true,
    defaultValue: false,
    cases: [
        {
            caseValue: true,
            children: [
                {
                    type: 'azure-key-vault',
                    bind: 'referedAKV',
                    isLabelVertical: true
                }
            ]
        },
        {
            caseValue: false,
            children: [
                ...connectionStringControls.map(parameterizeDynamicsControlMetadata),
                {
                    type: 'key-val-list',
                    bind: 'additionalFields',
                    label: stringResources.AdditionalConnectionStringFields
                }
            ]
        }
    ]
};

export const mariaDBMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.mariaDB.linkedServiceType,
        displayName: stringResources.MariaDB_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-maria_db'
        },
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'MariaDBForPipeline'
        },
        supportedAuthKindsInTrident: [
            ConnectionAuthKind.Basic
        ],
        supportCopyJobSource: true,
        supportMultipleIncrementalColumns: true,
        supportOPDG: GlobalVariables.enableOPDG,
        isFirstClass: true,
        supportDatasetV2: true,
        category: ['Database'],
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.mariaDB.copySourceType],
        supportFirstClassParameterExperience: true,
        isDeprecated: !GlobalVariables.showLegacy ? '{ driverVersion !== "v2" }' : false,
        showWarningMessage: '{ driverVersion !== "v2" }',
        warningMessage: format(
            stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
            stringResources.DriverVersion.toLocaleLowerCase()
        ),
        warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-mariadb',
        controls: [
            {
                type: 'radio-switch',
                bind: 'driverVersion',
                label: stringResources.DriverVersion,
                isLabelVertical: true,
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                hide: !GlobalVariables.showLegacy ? '{driverVersion === "v2" }' : false,
                cases: [
                    {
                        caseValue: driverVersions.v2,
                        children: [
                            ...connectionStringControls.map(parameterizeDynamicsControlMetadata),
                            sslModeContol
                        ],
                    },
                    {
                        caseValue: driverVersions.v1,
                        children: [connectionStrAKVControl]
                    }
                ]
            },
            ...(connectionStringControls
                .map(parameterizeDynamicsControlMetadata)
                .map((control) => ({
                    ...control,
                    hide: !GlobalVariables.showLegacy ? '{driverVersion !== "v2" }' : true
                }))
            ),
            sslModeContol
        ]
    };
    return metadata;
};
